import { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'src/state/reduxStore';
import { appSettings } from '../common';
import { isLocalizationEnabled } from '../components/utilities/Localization/Utils';
import { useAuth } from './useAuth';

const COUNTRY_STORAGE_PREFIX = 'npp:contactUsAgentCountry:';

interface IContactUsAgentState {
  isCopilotEnabled: boolean;
  copilotConfigLoaded: boolean;
  authenticationRequired: boolean;
  allowedCountries: string[];
  nonprofitCountryCode: string | undefined;
  nonprofitIsLoading: boolean;
  nonprofitMiniProfileLoaded: boolean;
  localizationReady: boolean;
}

interface IContactUsAgentEligibility extends IContactUsAgentState {
  storedCountry: string | undefined;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
}

const normalizeCountryCode = (countryCode: string | undefined): string | undefined => countryCode?.toLowerCase();

const getStoredCountry = (userId: string | undefined): string | undefined => {
  if (!userId) {
    return undefined;
  }
  try {
    return localStorage.getItem(COUNTRY_STORAGE_PREFIX + userId) ?? undefined;
  } catch {
    return undefined;
  }
};

const setStoredCountry = (userId: string, countryCode: string): void => {
  try {
    localStorage.setItem(COUNTRY_STORAGE_PREFIX + userId, countryCode);
  } catch {
    // Ignore storage errors (e.g., disabled localStorage).
  }
};

const useStoredCountry = (
  userId: string | undefined,
  isAuthenticated: boolean,
  nonprofitCountryCode: string | undefined
): string | undefined => {
  const [storedCountry, setStoredCountryState] = useState(() => getStoredCountry(userId));

  useEffect(() => {
    setStoredCountryState(getStoredCountry(userId));
  }, [userId]);

  useEffect(() => {
    const normalizedCountryCode = normalizeCountryCode(nonprofitCountryCode);

    if (!isAuthenticated || !userId || !normalizedCountryCode) {
      return;
    }

    setStoredCountry(userId, normalizedCountryCode);
    setStoredCountryState(normalizedCountryCode);
  }, [isAuthenticated, userId, nonprofitCountryCode]);

  return storedCountry;
};

const selectContactUsAgentState = (state: RootState): IContactUsAgentState => ({
  isCopilotEnabled: state.flags.ContactUsAgentEnabled,
  copilotConfigLoaded: state.agentVerification.copilotConfigLoaded,
  authenticationRequired: state.agentVerification.copilotConfig.authenticationRequired,
  allowedCountries: state.agentVerification.copilotConfig.allowedCountries,
  nonprofitCountryCode: state.nonprofit?.miniProfile?.address?.countryCode,
  nonprofitIsLoading: !!state.nonprofit?.isLoading,
  nonprofitMiniProfileLoaded: !!state.nonprofit?.miniProfile,
  // When localization is enabled, the CopilotKit chrome reads localized text, so it must not
  // mount until the locale content has loaded. When localization is disabled, content is read
  // synchronously, so it is always considered ready.
  localizationReady: !isLocalizationEnabled() || state.locale.isSuccessfulRetrival === true
});

const getContactUsAgentResult = ({
  isCopilotEnabled,
  copilotConfigLoaded,
  authenticationRequired,
  allowedCountries,
  nonprofitCountryCode,
  storedCountry,
  nonprofitIsLoading,
  nonprofitMiniProfileLoaded,
  isAuthenticated,
  isAuthLoading,
  localizationReady
}: IContactUsAgentEligibility): ShowContactUsAgentResult => {
  const baseEligible = isCopilotEnabled && !!appSettings.copilotRuntimeUrl;
  if (!baseEligible) {
    return { showAgent: false, isLoading: false };
  }

  // The CopilotKit chrome reads localized text. When localization is enabled, wait until the
  // locale content has loaded before showing the agent so it does not render with missing
  // strings on a cold load.
  if (!localizationReady) {
    return { showAgent: false, isLoading: true };
  }

  // The eligibility decision depends on values from the copilot config
  // (authentication requirement, allowed countries). Until the config has
  // loaded those values are defaults, so report loading and decide later.
  if (!copilotConfigLoaded) {
    return { showAgent: false, isLoading: true };
  }

  if (!authenticationRequired) {
    return { showAgent: true, isLoading: false };
  }

  if (!isAuthenticated) {
    return { showAgent: false, isLoading: isAuthLoading };
  }

  const effectiveCountry = normalizeCountryCode(nonprofitCountryCode) ?? storedCountry;

  if (effectiveCountry) {
    return {
      showAgent: allowedCountries.includes(effectiveCountry),
      isLoading: false
    };
  }

  if (nonprofitIsLoading || !nonprofitMiniProfileLoaded) {
    return { showAgent: false, isLoading: true };
  }

  return { showAgent: false, isLoading: false };
};

export interface ShowContactUsAgentResult {
  /** True when CopilotKit should be rendered for the current user. */
  showAgent: boolean;
  /**
   * True when we cannot yet decide whether to show the agent. This covers the
   * window before the copilot config has loaded, as well as while the
   * authenticated user's nonprofit data (and therefore country) is still being
   * loaded. Callers should render a spinner instead of either page variant
   * while this is true to avoid flicker.
   */
  isLoading: boolean;
}

/**
 * Returns whether CopilotKit should be rendered for the current user, plus a
 * loading flag so the caller can avoid rendering the legacy Contact Us page
 * while we are still determining the user's country.
 *
 * Conditions for `showAgent`:
 *  - The `ContactUsAgentEnabled` feature flag is on.
 *  - A copilot runtime URL is configured.
 *  - When localization is enabled, the locale content has finished loading
 *    (so the CopilotKit chrome does not render with missing strings).
 *  - If the copilot config requires authentication, the user is authenticated
 *    and their country (from nonprofit data, or cached in localStorage from a
 *    previous load) is in the allowed list.
 */
export const useShowContactUsAgent = (): ShowContactUsAgentResult => {
  const contactUsAgentState = useSelector(selectContactUsAgentState, shallowEqual);
  const { nonprofitCountryCode } = contactUsAgentState;
  const { isAuthenticated, isLoading: isAuthLoading, account } = useAuth();
  const userId = account?.homeAccountId;
  const storedCountry = useStoredCountry(userId, isAuthenticated, nonprofitCountryCode);

  return getContactUsAgentResult({
    ...contactUsAgentState,
    storedCountry,
    isAuthenticated,
    isAuthLoading
  });
};

